import { getCsrfToken } from '@ctct/ctct-helpers';

import { getAccountData, getCustomerAudit } from './DistUI';
import getBaseUrl from './getBaseUrl';
import axios from '../axiosConfig';
import AddOnIds from '../constants/addOnIds';
import { findSmsAddOnInList } from '../utils/smsAddOnUtils';

/* This file was created to allow just the some functions to be available in
 * the myaccount-utils-static library. This was done to avoid refactoring all of
 * MyAccountApi.js (imports, dependencies, unit tests) in myact-react-static. If more
 * functionality is needed in myaccount-utils-static, then we may want to consider just
 * merging all of the MyAccountApi functionality into myaccount-utils-static.
 */

const getUrl = (path) => `${getBaseUrl()}myaccountapi${path[0] !== '/' ? '/' : ''}${path}`;

const token = getCsrfToken();
const DEFAULT_OPTIONS = {
  mode: 'cors',
  credentials: 'include',
  cache: 'no-store',
  redirect: 'follow',
  headers: {
    'X-CSRF-Token': token,
    'X-Requested-With': 'XMLHttpRequest',
    'content-type': 'application/json',
  },
};

const MyAccountApiLite = {
  setRole: (userId, roleId) => axios.put(getUrl(`/billing/customers/users/${userId}/roles`), [roleId], DEFAULT_OPTIONS),
  deactivateUser: (userId) =>
    axios.put(getUrl(`/account/customers/users/${userId}/status`), { status: 'DISABLED', userId }, DEFAULT_OPTIONS),
  reactivateUser: (userId) =>
    axios.put(getUrl(`/account/customers/users/${userId}/status`), { status: 'ENABLED', userId }, DEFAULT_OPTIONS),
  getPlanRating: async (params) => {
    const accountData = await getAccountData();
    const {
      contactUsed: used,
      currentPlanId,
      customer,
      discount: { discountDescription: description, discountPercent } = {},
      discountCode,
      getActivePromotionCodes,
      newAddOnServices,
      paymentPlanGroupId: paymentPlanId,
      selectedCurrency: currencyCode,
      newPlanType,
      selectedPlan: { id: newPlanId } = {},
      smsUsed,
      paymentPeriod,
      stripeAccount,
    } = params;
    const { planDetail: { planGroupId } = {} } = accountData;
    const usageData = [{ usageType: 'CONTACTS', used: String(used || 0), resetValue: 0, minValue: null }];

    if (smsUsed != null) {
      usageData.push({ usageType: 'SMS', used: String(smsUsed), resetValue: 0, minValue: null });
    }

    const postBody = {
      includeTax: !!customer && !customer.vatNumber,
      customer: { ...customer, currencyCd: currencyCode },
      ...(stripeAccount ? { billingPeriod: 1 } : {}),
      currencyCode,
      planGroupId,
      currentPlanId,
      newPlanType,
      newPlanId,
      newAddOnServices,
      paymentPeriod: paymentPeriod || 0,
      paymentPlanId,
      usageData,
      stripeAccount,
    };

    if (getActivePromotionCodes) {
      postBody.activePromotionCodes = discountCode ? [discountCode] : [];
    } else {
      postBody.newPromotionCodes = discountCode ? [discountCode] : [];
    }

    try {
      const response = await axios.post(getUrl('/billing/customers/plan/rating'), postBody, DEFAULT_OPTIONS);
      const rating = response.data;

      return {
        ...rating,
        description,
        name: 'paymentPlan',
        discountPercent,
      };
    } catch (err) {
      return err;
    }
  },
  getCustomer: async () => (await axios.get(getUrl('/billing/customers'), DEFAULT_OPTIONS)).data,
  getBillingAccount: async () => (await axios.get(getUrl('/billing/customers/account'), DEFAULT_OPTIONS)).data,
  getBillingInfo: async () => (await axios.get(getUrl('/billing/customers/users/billinginfo'), DEFAULT_OPTIONS)).data,
  getPaymentPlan: async () => (await axios.get(getUrl('/billing/customers/paymentplan'), DEFAULT_OPTIONS)).data,
  getPlanGroup: async (currency, count = 0, usage = 0) => {
    const audit = await getCustomerAudit();

    if (audit) {
      const { createdOn } = audit;
      const currentTime = new Date().getTime();

      // More than 5 minutes ago account created.
      if (currentTime - createdOn > 60000 * 5) {
        return (
          await axios.get(
            getUrl(`/plan/customers/users/currency/${currency}/plan_group?count=${count}`),
            DEFAULT_OPTIONS
          )
        ).data;
      }

      return (
        await axios.get(
          getUrl(`/plan/customers/users/currency/${currency}/plan_group?count=${count}&usage=${usage}`),
          DEFAULT_OPTIONS
        )
      ).data;
    }

    throw new Error('DIST UI error');
  },
  getSMSTiers: async (currency, paymentPlanId) => {
    const {
      planDetail: { planGroupId },
    } = await getAccountData();

    try {
      const getAllAddOnsResults = (
        await axios.get(
          getUrl(
            `/plan/customers/addons/plangroups/${planGroupId}/plans/${paymentPlanId}?currencyCode=${currency}&includePurchased=true`
          ),
          DEFAULT_OPTIONS
        )
      ).data;
      const hasPriceTiers = (addOn) => Boolean(addOn?.priceTiers?.length);
      const smsAddOn = findSmsAddOnInList(getAllAddOnsResults, AddOnIds.CTCT_SMS_ADDON, hasPriceTiers);

      return smsAddOn?.priceTiers || [];
    } catch (err) {
      return [];
    }
  },
  getAddOns: async (currency, paymentPlanId) => {
    const {
      planDetail: { planGroupId },
    } = await getAccountData();

    try {
      const getAllAddOnsResults = (
        await axios.get(
          getUrl(
            `/plan/customers/addons/plangroups/${planGroupId}/plans/${paymentPlanId}?currencyCode=${currency}&includePurchased=true`
          ),
          DEFAULT_OPTIONS
        )
      ).data;

      const addOnsOrdering = [
        'CTCT-SMS-ADDON',
        'CTCT-MARKETING-ASSISTANT-102',
        'CTCT-INBOX-PREVIEW-101',
        'CTCT-SEO-101',
        'CTCT-ARCHIVE-101',
      ];

      const sortedAddOns =
        getAllAddOnsResults == null
          ? null
          : getAllAddOnsResults.sort((a, b) => {
              const indexA = addOnsOrdering.findIndex((ao) => ao === a.id);
              const indexB = addOnsOrdering.findIndex((ao) => ao === b.id);

              return indexA - indexB;
            });

      return sortedAddOns;
    } catch (err) {
      return [];
    }
  },
  getRMCValue: async (key) =>
    (await axios.get(getUrl(`/billing/utils/${key}/configurationvalue`), DEFAULT_OPTIONS)).data,
  payForPlan: (data) => {
    return fetch(getUrl(`/billing/customers/users/plan/pay`), {
      method: 'POST',
      ...DEFAULT_OPTIONS,
      body: JSON.stringify(data),
    });
  },
  getUserPlanOverview: async () => (await axios.get(getUrl(`/plan/customers/users/overview`), DEFAULT_OPTIONS)).data,
  getCancelReasons: async () => (await axios.get(getUrl('/billing/cancelreasons'), DEFAULT_OPTIONS)).data,
};

export default MyAccountApiLite;
